<template>
  <div :class="bemBlock" itemtype="http://schema.org/SomeProducts">
    <InfiniteLoading
      v-if="infinitePrevious"
      :force-use-infinite-wrapper="infiniteWrapper"
      :identifier="infiniteTopKey"
      direction="top"
      @infinite="infinitePrevHandler"
    >
      <!-- Hide spinner when going top -->
      <div slot="spinner" />
      <!-- Hide some messages. Empty slots will not be displayed -->
      <div slot="no-more" />
      <div slot="no-results" />
    </InfiniteLoading>
    <ul :class="bemElement('list')" itemtype="http://schema.org/SomeProducts">
      <template v-for="(tileData, index) in products">
        <li
          v-if="tileData.display.linebreak && numAppliedFilters === 0"
          :key="`${index}-linebreak`"
          :class="[bemElement('linebreak')]"
        />
        <li
          v-if="tileData.display.categorySeparator"
          :key="`${index}-linebreak`"
          v-observe-visibility="{
            callback: (isVisible, entry) =>
              observeCategorySeparator(entry, tileData.categories),
            intersection: {
              rootMargin: `-${headerHeight}px 0px 0px 0px`,
              threshold: 1,
            },
            once: false,
          }"
          :class="[bemElement('linebreak'), 'intersection']"
          :data-page-url="tileData.categories.next.url"
        />
        <li
          v-if="index === products.length - numItemsBasedBreakpoint"
          :key="`infinite-loading-wrapper-${index}`"
          :class="[bemElement('hidden-trigger')]"
        >
          <InfiniteLoading
            :force-use-infinite-wrapper="infiniteWrapper"
            @infinite="infiniteNextHandler"
          >
            <!-- Hide all messages. Empty slots will not be displayed -->
            <div slot="spinner" />
            <div slot="no-more" />
            <div slot="no-results" />
          </InfiniteLoading>
        </li>
        <li
          v-if="shouldShowBanner(tileData)"
          :key="`grid-banner-wrapper-${index}`"
          :data-key="`grid-banner-wrapper-${index}`"
          :class="[
            bemElement('item', {
              'double-width': isBannerDoubleWidth(tileData),
              minimal: useMinimalTiles,
              [paramCase(tileData.product.banner.type)]: true,
            }),
            'grid-banner-wrapper',
          ]"
        >
          <TheGridBanner
            :keyid="`grid-banner-${index}`"
            :force-main-image-to-load="
              index <= indexOfLatestTileToPreloadMainImage
            "
            :grid-index="index"
            :grid-type="type"
            v-bind="tileData.display"
            :minimal="useMinimalTiles"
            :product="tileData.product"
          />
        </li>
        <!-- Treat every single slider banner product as an independent product
        for desktop and minimal tiles -->
        <template v-if="isSliderBannerDesktop(tileData)">
          <li
            v-for="(sliderProduct, sliderIndex) in tileData.product.banner
              .productSlider"
            :key="`product-tile-wrapper-${sliderProduct.id}-${index}-${sliderIndex}`"
            :data-key="`product-tile-wrapper-${sliderProduct.id}-${index}-${sliderIndex}`"
            :class="[
              bemElement('item', {
                minimal: useMinimalTiles,
              }),
              'product-tile-wrapper',
            ]"
          >
            <BaseProductTile
              :analytics="
                buildAnalyticsPropForProduct({
                  location: analyticsConst.location.plpSliderBanner,
                  position: index + (sliderIndex + 1) / 10,
                })
              "
              :are-carousel-controls-shown="shouldShowCarouselControls"
              :keyid="`${sliderProduct.id}-${index}-${sliderIndex}`"
              :context="getTileContext(sliderProduct)"
              :force-main-image-to-load="
                index <= indexOfLatestTileToPreloadMainImage
              "
              :grid-index="sliderIndex"
              :grid-type="type"
              :minimal="useMinimalTiles"
              :product="sliderProduct"
              :url="sliderProduct.selectedProductUrl || ''"
              :show-swatches="false"
              @load-main-image="handleLoadMainImage(index)"
            />
          </li>
        </template>
        <li
          v-else-if="tileData.product && !tileData.product.banner"
          :key="`product-tile-wrapper-${tileData.product.id}-${index}`"
          :data-key="`product-tile-wrapper-${tileData.product.id}-${index}`"
          :class="[
            bemElement('item', {
              'double-width': isTileDoubleWidth(tileData.display.doublewidth),
              minimal: useMinimalTiles,
            }),
            'product-tile-wrapper',
          ]"
        >
          <BaseProductTile
            :are-carousel-controls-shown="shouldShowCarouselControls"
            :keyid="`${tileData.product.id}-${index}`"
            :analytics="
              buildAnalyticsPropForProduct({
                position: index,
              })
            "
            :context="getTileContext(tileData)"
            :force-main-image-to-load="
              index <= indexOfLatestTileToPreloadMainImage
            "
            :grid-index="index"
            :grid-type="type"
            :minimal="useMinimalTiles"
            :product="tileData.product"
            :url="tileData.urls.product"
            :show-swatches="shouldShowSwatches(tileData)"
            @load-main-image="handleLoadMainImage(index)"
            @open-pdp-modal="(product) => $emit('open-pdp-modal', product)"
          />
        </li>
      </template>
    </ul>
    <slot name="afterList" />
    <InfiniteLoading
      v-if="infiniteNext"
      :force-use-infinite-wrapper="infiniteWrapper"
      :identifier="infiniteBottomKey"
      @infinite="infiniteNextHandler"
    >
      <div slot="spinner">
        <span v-if="bottomSpinner !== null" class="interaction--loader-grid">
          {{ bottomSpinner }}</span
        >
        <BaseSpinner v-if="bottomSpinner === null" inline />
      </div>
      <!-- Hide some messages. Empty slots will not be displayed -->
      <div slot="no-more" />
      <div slot="no-results" />
    </InfiniteLoading>
  </div>
</template>

<script>
import { paramCase } from 'change-case';
import debounce from 'lodash/debounce';
import nanoid from 'nanoid/non-secure';
import InfiniteLoading from 'vue-infinite-loading';
import { mapGetters, mapState } from 'vuex';
import { getters } from '../store';
import TheGridBanner from './TheGridBanner.vue';
import BaseProductTile from './BaseProductTile.vue';
import BaseSpinner from './BaseSpinner.vue';
import analyticsMixin from '../mixins/analyticsMixin';
import bemClassesMixin from '../mixins/bemClassesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import { createIntersectionObserverHandler } from '../../common/utils/intersectionObserver';
import isMatchedBreakpoint from '../../common/utils/isMatchedBreakpoint';
import getFrontConfig from '../../common/getFrontConfig';

const {
  responsive: { bp_768: bp768, bp_1025: bp1025 },
  vue: {
    BaseProductTile: { context },
    TheProductList: { bannerTypes, columnMode },
  },
} = getFrontConfig();

export default {
  name: 'BaseProductGrid',

  components: {
    TheGridBanner,
    BaseProductTile,
    BaseSpinner,
    InfiniteLoading,
  },

  mixins: [analyticsMixin, bemClassesMixin, windowResizeMixin],

  props: {
    headerHeight: {
      type: Number,
      default: 0,
    },
    columnMode: {
      type: String,
      validator: (prop) =>
        [columnMode.default, columnMode.compressed].includes(prop),
      default: columnMode.default,
    },
    products: {
      type: Array,
      default() {
        return [];
      },
    },
    infiniteNext: {
      type: Boolean,
      default: false,
    },
    infiniteWrapper: {
      type: [Boolean, String],
      default: false,
    },
    infinitePrevious: {
      type: Boolean,
      default: false,
    },
    showSwatches: {
      type: Boolean,
      default: true,
      required: false,
    },
    type: {
      type: String,
      validator: (prop) =>
        ['crossSelling', 'productList', 'search'].includes(prop),
      required: false,
      default: null,
    },
    topSpinner: {
      type: String,
      default: null,
    },
    bottomSpinner: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      categoryShown: null,
      dataChangeBreakpoint: bp1025,
      infiniteKeyBase: nanoid(),
      infiniteKeyToRerender: 1,
      indexOfLatestTileWithLoadedMainImage: -1, // Loaded images control
      paramCase,
    };
  },

  computed: {
    ...mapState({
      searchQuery: (state) => state.search.query,
    }),
    ...mapGetters({
      numAppliedFilters: getters.productList.numAppliedFilters,
    }),
    // Max index to force main image loading
    indexOfLatestTileToPreloadMainImage() {
      const numOfMainImagesToPreload = 4;
      return (
        this.indexOfLatestTileWithLoadedMainImage + numOfMainImagesToPreload
      );
    },
    infiniteBottomKey() {
      return `infinite-bottom-${this.infiniteKeyBase}-${this.infiniteKeyToRerender}`;
    },
    infiniteTopKey() {
      return `infinite-top-${this.infiniteKeyBase}-${this.infiniteKeyToRerender}`;
    },
    numItemsBasedBreakpoint() {
      // returns 4 in case is desktop, 2 in mobile
      return isMatchedBreakpoint.matches('bp_1025', 'min-width') ? 4 : 2;
    },
    /**
     * Manage if the controls of the carousels (arrows) should be rendered.
     * Cases:
     * - Mobile: Only in default column mode. With minimal tiles there is no
     *   space for arrows and dragging.
     * - Desktop: Always available
     * @returns {boolean} show controls
     */
    shouldShowCarouselControls() {
      return this.isBreakpointDesktop || !this.useMinimalTiles;
    },
    useMinimalTiles() {
      return this.columnMode === columnMode.compressed;
    },
  },

  watch: {
    /**
     * Watch categoryShown to emit the corresponding event
     * Debounce is used because all previous categories will modify the data.
     * Why?
     * As user can scroll very quickly, observeCategorySeparator() method cannot
     * define any limit to detect top interactions. Otherwise, the component can
     * miss a category that is currently shown in PLP, but no detected by the
     * observer handler.
     */
    categoryShown: debounce(function emitCategoryShownEvent(val) {
      this.$emit('category-shown', val);
    }, 100),
    products() {
      this.infiniteReset();
    },
  },

  mounted() {
    this.observeCategorySeparator = createIntersectionObserverHandler({
      inCallback: (
        entry,
        { previous } = {
          next: null,
          previous: null,
        }
      ) => {
        const direction = entry.target.getAttribute(
          'data-in-viewport-direction'
        );
        const position = entry.target.getAttribute('data-in-viewport-position');
        const isPreviousCategoryViewed =
          direction === 'from-above' && position === 'intersecting';
        if (isPreviousCategoryViewed) {
          this.categoryShown = previous;
        }
      },
      outCallback: (
        entry,
        { next } = {
          next: null,
          previous: null,
        }
      ) => {
        const direction = entry.target.getAttribute(
          'data-in-viewport-direction'
        );
        const position = entry.target.getAttribute('data-in-viewport-position');
        const isNextCategoryViewed =
          direction === 'from-intersecting' && position === 'above';
        if (isNextCategoryViewed) {
          this.categoryShown = next;
        }
      },
    });
    // Emit custom mounted event, in order to detect mounting whenever this
    // component is dinamically imported
    this.$emit('mounted');
  },

  methods: {
    buildAnalyticsPropForProduct({ location, position }) {
      return this.analyticsExtendProp({
        location,
        position,
      });
    },
    getTileContext(tileData) {
      let result;
      if (this.type === 'search') {
        result = context.search;
      } else if (this.type === 'crossSelling') {
        result = context.crossSellingGrid;
      } else {
        result = this.isTileDoubleWidth(tileData.display?.doublewidth)
          ? context.plpGridFullWidth
          : context.plpGrid;
      }
      return result;
    },
    handleLoadMainImage(tileIndex) {
      this.indexOfLatestTileWithLoadedMainImage = Math.max(
        this.indexOfLatestTileWithLoadedMainImage,
        tileIndex
      );
    },
    infinitePrevHandler($state) {
      this.$emit('infinite:previous', $state);
    },
    infiniteNextHandler($state) {
      this.$emit('infinite', $state);
      this.$emit('infinite:next', $state);
    },
    infiniteReset() {
      this.infiniteKeyToRerender += 1;
    },
    isBannerDoubleWidth(tileData) {
      return (
        tileData.product?.banner?.type === bannerTypes.plpMobileSlider &&
        this.$windowWidth < bp768
      );
    },
    isSliderBannerDesktop(tileData) {
      return (
        tileData.product?.banner?.type === bannerTypes.plpMobileSlider &&
        (this.$windowWidth >= bp768 || this.useMinimalTiles) &&
        tileData.product?.banner?.productSlider?.length
      );
    },
    isTileDoubleWidth(doublewidth) {
      return doublewidth === true && this.numAppliedFilters === 0;
    },
    shouldShowBanner(tileData) {
      if (
        this.$windowWidth >= bp768 &&
        tileData.product?.banner?.type === bannerTypes.plpMobileSlider
      ) {
        return false;
      }
      return [
        bannerTypes.plpSimpleImage,
        bannerTypes.plpDoubleImage,
        bannerTypes.plpMobileSlider,
        bannerTypes.plpFullImage,
      ].includes(tileData.product?.banner?.type);
    },
    shouldShowSwatches(tileData) {
      if (this.showSwatches === false || this.useMinimalTiles) return false;

      const doublewidth = this.isTileDoubleWidth(tileData.display?.doublewidth);
      return this.isBreakpointDesktop || doublewidth;
    },
  },
};
</script>
